import {
  forwardRef,
  ReactNode,
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';

interface Props {
  onClickOutside: () => void;
  children?: ReactNode;
  ref?: Ref<any> | Ref<any>[];
  [key: string]: any;
}

function useLocalClickOutside(
  onClickOutside: Props['onClickOutside'],
  ref?: Props['ref']
) {
  const callbackRef = useRef(onClickOutside);
  useEffect(() => {
    callbackRef.current = onClickOutside;
  }, [onClickOutside]);

  const localRef = useRef(null);
  const refs = useRef<RefObject<any>[]>([]);
  useEffect(() => {
    if (!Array.isArray(ref)) {
      refs.current = [(ref || localRef) as RefObject<any>];
    } else {
      refs.current = ref as RefObject<any>[];
    }
  }, [ref, localRef]);

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (
        refs.current.every(
          (r) => r && r.current && !r.current.contains(event.target)
        )
      ) {
        callbackRef.current();
      }
    },
    [callbackRef, refs]
  );
  useEffect(() => {
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClick]);
  return localRef;
}

export const ClickOutside = forwardRef<any, Props>(
  ({ children, onClickOutside, ...otherProps }: Props, ref) => {
    const target = useLocalClickOutside(onClickOutside, ref);
    if (ref) {
      return <>{children}</>;
    }
    return (
      <div {...otherProps} ref={target}>
        {children}
      </div>
    );
  }
);

export function useClickOutside(
  onClickOutside: Props['onClickOutside'],
  refs: Ref<any> | Ref<any>[] = null
) {
  return useLocalClickOutside(onClickOutside, refs);
}
